import { useEffect, type FC } from "react"
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import dayjs from "dayjs"

import ProposalShareMenuItem from "proposal/components/ctas/ProposalShareMenuItem"
import ProposalCancelButton from "proposal/components/ctas/ProposalCancelButton"
import ProposalBlockExplorerURLButton from "proposal/components/ctas/ProposalBlockExplorerURLButton"
import { ROUTES } from "common/constants/routes"
import {
  ProposalActionAttemptDocument,
  ProposalActionType,
  useProposalPendingCtaQuery,
  ProposalStatus,
} from "query/graphql"
import type {
  Governor,
  Proposal,
  ProposalActionAttemptQuery,
  ProposalActionAttemptQueryVariables,
} from "query/graphql"
import { getGovernorProposalTxHash } from "proposal/helpers/status"
import { Clock } from "ui/components/icons/font-awesome/Clock"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import ShareToFarcasterButton from "common/components/ShareToFarcasterButton"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useDescriptionException } from "proposal/hooks/useDescriptionException"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import ShareToLensButton from "common/components/ShareToLensButton"
import { GrpcErrorType } from "common/helpers/fetcher"
import { useQuery } from "query/hooks/useQuery"
import { useRouter } from "common/hooks/useRouter"

type ProposalPendingCTAProps = {
  proposalId: string
  slug: string
}
const ProposalPendingCTA: FC<ProposalPendingCTAProps> = ({
  proposalId,
  slug,
}) => {
  const { reload } = useRouter()
  const { data, isLoading } = useProposalPendingCtaQuery(
    {
      input: { id: proposalId },
    },
    {
      refetchInterval: (data) => {
        const block = data?.proposal.start

        if (block?.timestamp) {
          if (dayjs().isBefore(dayjs(block.timestamp))) {
            const now = dayjs()
            const blockTimestamp = dayjs(block.timestamp)
            const differenceInSeconds = blockTimestamp.diff(now, "second")

            // Diff is less than 60 seconds, refresh every 30 seconds
            if (differenceInSeconds < 60) {
              return 30000
            }

            // Diff is less than 30 minutes, refresh every 2 minutes
            if (differenceInSeconds < 1800) {
              return 120000
            }

            // Refresh every 1 hour
            return 3600000
          }

          // Time is gone, refresh every 30 seconds
          return 30000
        }

        return false
      },
    },
  )

  const internalProposalId = data?.proposal?.id ?? undefined

  const { data: proposalActionAttemptData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >(
    {
      query: ProposalActionAttemptDocument,
      variables: {
        input: {
          proposalId: internalProposalId ?? "",
          type: ProposalActionType.Cancel,
        },
      },
      omittedErrors: [GrpcErrorType.NotFound],
    },
    { enabled: internalProposalId !== undefined },
  )

  useEffect(() => {
    if (data && data.proposal) {
      // If the status is now Active, then reload the page
      if (data.proposal.status === ProposalStatus.Active) {
        reload()

        return
      }
    }
  }, [data, reload])

  if (proposalActionAttemptData) {
    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          direction="row"
          spacing={3}
          width={{ base: "100%", sm: "auto" }}
        >
          <Spinner />
          <Text textStyle="body.bold.lg">Canceling Proposal</Text>
        </Stack>
      </Stack>
    )
  }

  const block = data?.proposal.start

  const getVotingStartsLabel = () => {
    if (block?.timestamp) {
      // Display correct time only if block's timestamp is in a future
      // This prevents displaying "Voting starts a minute ago" in DAOs
      // what has a proposal delay of seconds
      if (dayjs().isBefore(dayjs(block.timestamp))) {
        const dateLabel = dayjs(block.timestamp).fromNow()

        return `Voting starts ${dateLabel}`
      }

      return "Voting starts soon"
    }

    return "Calculating voting start..."
  }

  const votingStartsLabel = getVotingStartsLabel()

  if (isLoading) return null

  if (!data) return null

  const { proposal } = data

  return (
    <Stack
      align="center"
      color="gray.600"
      direction="row"
      justify={{ base: "center", lg: "right" }}
      spacing={4}
      width="100%"
    >
      <Stack
        align="center"
        direction="row"
        width={{ base: "100%", sm: "auto" }}
      >
        <Icon as={Clock} fill="gray.600" />

        <Text textStyle="body.bold.lg">{votingStartsLabel}</Text>
      </Stack>
      <OptionsList
        governance={proposal.governor as Governor}
        proposal={proposal as Proposal}
        slug={slug}
      />
    </Stack>
  )
}

type OptionsListProps = {
  governance: Governor
  proposal: Proposal
  slug: string
}
const OptionsList: FC<OptionsListProps> = ({ governance, proposal, slug }) => {
  const { organization } = useOrganization()
  const { title } = useDescriptionException({
    governanceId: governance.id,
    proposal,
  })
  const { isFeatureFlagOn } = useGovernanceContext()
  const hideCancelProposal = isFeatureFlagOn(FeatureFlag.HideCancelProposal)
  const txHash = getGovernorProposalTxHash(proposal)

  return (
    <Flex>
      <Menu>
        <MenuButton
          as={Button}
          bgColor="white"
          borderColor="gray.300"
          borderWidth="1px"
          px={3}
        >
          <Icon as={EllipsisVertical} fill="gray.600" />
        </MenuButton>
        <MenuList>
          {organization && proposal?.onchainId ? (
            <ProposalShareMenuItem
              url={ROUTES.governance.proposal.id(
                slug,
                proposal.onchainId,
                organization.governorIds.length && proposal.governor
                  ? proposal.governor.id
                  : "",
              )}
            />
          ) : null}

          {organization && title ? (
            <>
              <MenuItem>
                <ShareToFarcasterButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
              <MenuItem>
                <ShareToLensButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
            </>
          ) : null}

          {txHash ? (
            <MenuItem>
              <ProposalBlockExplorerURLButton
                chainId={governance.chainId}
                txHash={txHash}
              />
            </MenuItem>
          ) : null}

          {!hideCancelProposal ? (
            <MenuItem>
              <ProposalCancelButton
                governance={governance as Governor}
                proposal={proposal}
              />
            </MenuItem>
          ) : null}
        </MenuList>
      </Menu>
    </Flex>
  )
}

export default ProposalPendingCTA
